<template>
  <div class="bg-white rounded-lg shadow-md border border-gray-200">
    <div class="px-6 py-4 border-b border-gray-200">
      <div class="flex justify-between items-center gap-3">
        <div>
          <h3 class="text-lg font-semibold text-gray-800">
            {{ isManager ? 'Licencias del día' : 'Mis licencias del día' }}
          </h3>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ isManager ? 'Personal con ausencias en el rango seleccionado' : 'Tus ausencias en el rango seleccionado' }}
          </p>
        </div>
        <button
          type="button"
          @click="fetchRequests"
          class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          :disabled="loading"
          title="Actualizar"
        >
          <svg class="h-5 w-5" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
        </button>
      </div>

      <div class="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label class="block text-xs font-semibold text-gray-500 mb-1">Estado</label>
          <select v-model="filters.status" @change="fetchRequests" class="field">
            <option v-for="status in statuses" :key="status.value || 'all'" :value="status.value">
              {{ status.label }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 mb-1">Desde</label>
          <input v-model="filters.from" @change="onDateChange('from')" type="date" class="field" />
        </div>
        <div>
          <label class="block text-xs font-semibold text-gray-500 mb-1">Hasta</label>
          <input v-model="filters.to" @change="onDateChange('to')" type="date" class="field" />
        </div>
      </div>

      <div class="mt-3 flex flex-wrap gap-2">
        <button type="button" @click="setToday" class="chip-btn" :class="{ 'chip-btn-active': isTodayRange }">
          Hoy
        </button>
        <button type="button" @click="resetFilters" class="chip-btn">
          Restablecer
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <span class="ml-3 text-gray-600">Cargando licencias...</span>
    </div>

    <div v-else-if="error" class="px-6 py-4">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 class="text-sm font-medium text-red-800">Error al cargar licencias</h3>
        <p class="text-sm text-red-700 mt-1">{{ error }}</p>
        <button type="button" @click="fetchRequests" class="mt-2 text-sm font-semibold text-red-800 underline">
          Reintentar
        </button>
      </div>
    </div>

    <div v-else-if="requests.length === 0" class="px-6 py-10 text-center">
      <div class="mx-auto w-12 h-12 rounded-full bg-green-50 text-green-700 flex items-center justify-center mb-3">
        <i class="fa-regular fa-circle-check text-xl"></i>
      </div>
      <p class="text-sm font-medium text-gray-800">No hay licencias en este período</p>
      <p class="text-xs text-gray-500 mt-1">Probá cambiando el estado o el rango de fechas.</p>
    </div>

    <div v-else class="divide-y divide-gray-200 max-h-96 overflow-y-auto">
      <button
        v-for="request in requests"
        :key="requestId(request)"
        type="button"
        class="w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors duration-200"
        @click="openRequest(request)"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2 mb-1">
              <h4 class="text-sm font-semibold text-gray-900 truncate">
                {{ requestTeacherName(request) }}
              </h4>
              <span
                class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border"
                :class="statusClass(request.status)"
              >
                {{ statusLabel(request.status) }}
              </span>
            </div>
            <p v-if="requestTeacherMeta(request)" class="text-xs text-gray-500 mb-1">
              {{ requestTeacherMeta(request) }}
            </p>
            <p class="text-sm text-gray-600 truncate">{{ requestTitle(request) }}</p>
            <p class="text-xs text-gray-500 mt-1">
              {{ dateRange(request) }}
              <span v-if="requestedDays(request)"> · {{ requestedDays(request) }}</span>
            </p>
          </div>
          <i class="fa-solid fa-chevron-right text-gray-300 mt-1 shrink-0"></i>
        </div>
      </button>
    </div>

    <div v-if="!loading && !error && requests.length > 0" class="px-6 py-3 border-t border-gray-200 bg-gray-50">
      <p class="text-xs text-gray-500 text-center">
        {{ requests.length }} {{ requests.length === 1 ? 'licencia' : 'licencias' }}
      </p>
    </div>
  </div>
</template>

<script>
import { leaveRequestsApi } from '@/service/leave.service';
import {
  LEAVE_STATUSES,
  formatDate,
  isLeaveManagerRole,
  requestTeacherMeta,
  requestTeacherName,
  requestTitle,
  statusClass,
  statusLabel,
  todayISO,
  unwrapList,
} from '@/utils/leave';

export default {
  name: 'DashboardLeaveRequests',
  data() {
    const today = todayISO();
    return {
      loading: false,
      error: '',
      requests: [],
      filters: {
        status: '',
        from: today,
        to: today,
      },
      statuses: LEAVE_STATUSES,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters['authentication/currentUser'];
    },
    currentUserId() {
      return this.currentUser?.user_id;
    },
    isManager() {
      return isLeaveManagerRole(this.currentUser?.role);
    },
    isTodayRange() {
      const today = todayISO();
      return this.filters.from === today && this.filters.to === today;
    },
  },
  created() {
    this.fetchRequests();
  },
  methods: {
    statusClass,
    statusLabel,
    requestTitle,
    requestTeacherName,
    requestTeacherMeta,
    requestId(request) {
      return request.id || request.request_id;
    },
    dateRange(request) {
      const start = formatDate(request.start_date);
      const end = formatDate(request.end_date);
      return start === end ? start : `${start} → ${end}`;
    },
    requestedDays(request) {
      const days = request.requested_days ?? request.days ?? request.total_days;
      if (!days) return '';
      return `${days} ${Number(days) === 1 ? 'día' : 'días'}`;
    },
    onDateChange(field) {
      if (this.filters.from && this.filters.to && this.filters.from > this.filters.to) {
        if (field === 'from') this.filters.to = this.filters.from;
        else this.filters.from = this.filters.to;
      }
      this.fetchRequests();
    },
    setToday() {
      const today = todayISO();
      this.filters.from = today;
      this.filters.to = today;
      this.fetchRequests();
    },
    resetFilters() {
      const today = todayISO();
      this.filters = {
        status: 'approved',
        from: today,
        to: today,
      };
      this.fetchRequests();
    },
    openRequest(request) {
      const id = this.requestId(request);
      if (!id) return;
      const path = this.isManager && request.status === 'pending'
        ? `/leave/requests/${id}/review`
        : `/leave/requests/${id}`;
      this.$router.push(path);
    },
    async fetchRequests() {
      if (!this.isManager && !this.currentUserId) return;
      this.loading = true;
      this.error = '';
      try {
        const params = {
          status: this.filters.status,
          from: this.filters.from,
          to: this.filters.to,
        };
        if (!this.isManager) {
          params.user_id = this.currentUserId;
        }
        const response = await leaveRequestsApi.list(params);
        this.requests = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar las licencias.';
        this.requests = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.chip-btn {
  @apply px-3 py-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors;
}
.chip-btn-active {
  @apply bg-indigo-100 text-indigo-800 hover:bg-indigo-100;
}

/*
 * @apply bakes fixed light-mode colors into these scoped classes, so the
 * global dark-mode retrofit in style.css (which only overrides classes
 * literally present in the DOM) can never reach them. Same story for a few
 * status badge colors below that the retrofit doesn't cover for anyone.
 */
.dark .field {
  @apply bg-gray-700 border-gray-600 text-gray-100;
}
.dark .chip-btn {
  @apply bg-gray-700 text-gray-300;
}
.dark .chip-btn:hover {
  @apply bg-gray-600;
}
.dark .chip-btn-active {
  background-color: rgba(99, 102, 241, 0.25);
  color: theme('colors.indigo.200');
}
.dark .chip-btn-active:hover {
  background-color: rgba(99, 102, 241, 0.25);
}

.dark .bg-yellow-100 {
  background-color: rgba(234, 179, 8, 0.2);
}
.dark .bg-orange-100 {
  background-color: rgba(249, 115, 22, 0.2);
}
.dark .bg-blue-100 {
  background-color: rgba(59, 130, 246, 0.2);
}
.dark .text-yellow-800 {
  color: theme('colors.yellow.300');
}
.dark .text-orange-800 {
  color: theme('colors.orange.300');
}
.dark .text-blue-800 {
  color: theme('colors.blue.300');
}
.dark .border-red-200 {
  border-color: rgba(239, 68, 68, 0.35);
}
.dark .border-green-200 {
  border-color: rgba(34, 197, 94, 0.35);
}
.dark .border-yellow-200 {
  border-color: rgba(234, 179, 8, 0.35);
}
.dark .border-orange-200 {
  border-color: rgba(249, 115, 22, 0.35);
}
.dark .border-blue-200 {
  border-color: rgba(59, 130, 246, 0.35);
}
</style>
